import debounce from 'lodash.debounce';
import store from './player-store';
import {
  slides,
  slideContainer,
  currentSlide,
  stage,
  toolbar,
  getSlideSelectorByIndex,
} from './selectors';
import { isDocument, isEmbed, isInfographic, isPresentation } from "./presentation-types";
import { updateProgressBar } from './progress-bar';
import { hideEndDisplay, showEndDisplay } from './end-display';
import { isDesktop, isMobile, isTouchDevice } from '../utils/breakpoints';
import logEvent from '../tracking/amplitude/log-event';
import { updateClippingUI } from './clipping';
import { handlePlayerBlur } from './account-gate';

const FINISHED_MAJORITY_DEFINITION = 0.9;
const shareModalContainer = $('.modal-share');

const hasFinishedMajorityOfSlideshow = (currentSlideIndex, slideCount) => {
  const finishedIndex = Math.floor(slideCount * FINISHED_MAJORITY_DEFINITION);
  return currentSlideIndex === finishedIndex;
};

const updateIndex = (index) => {
  store.index = Math.max(0, Math.min(index, store.totalSlides - 1));

  // On mobile we have an end-trap div at the end
  if (isMobile() && index < store.totalSlides) {
    handleEndReach(false);
  }
};

const hideSlide = (index) => {
  getSlideByIndex(index).removeClass('current');
};

const preloadAround = (index) => {
  for (let i = index + 1; i <= Math.min(index + 3, store.totalSlides); i++) {
    const slideImage = getSlideByIndex(i).children("img");
    slideImage.attr("loading", "eager");
  }
}

const showSlide = (index) => {
  slides.removeClass('current'); // Prevent duplicate current
  getSlideByIndex(index).addClass('current');
  preloadAround(index);
};

export const scrollToSlide = (index, immediately = false) => {
  if (isInfographic()) return;

  const isDesktopDocument = isDocument() && isDesktop();
  const isEmbeddedDocument = isDocument() && isEmbed();
  const isEnteringFullScreen = immediately && !isMobile();
  const isMobileWithoutTouch = isMobile() && !isTouchDevice();
  const isMobileisEmbedded = isMobile() && isEmbed();
  if (isDesktopDocument || isEmbeddedDocument || isEnteringFullScreen) {
    // Immediate scroll with jQuery plugin
    stage.scrollTo(getSlideByIndex(index));
  } else if (isMobileWithoutTouch || isMobileisEmbedded) {
    // Both Presentation and Document behave the same way in mobile - horizontal scroll
    const slide = slides[index];

    slide.scrollIntoView(immediately ? {
      block: 'nearest'
    } : {
      behavior: "smooth", block: 'nearest'
    });
  }
};

export const getSlideByIndex = (index) => {
  return $(getSlideSelectorByIndex(index));
};

const setSlideButtonDisabledProp = (prefix, isDisabled) => {
  $(`#new-player #${prefix}-slide`).prop('disabled', isDisabled);
};

const updateNavigation = () => {
  const index = store.index;
  currentSlide.text(index + 1);

  if (index === store.totalSlides) {
    setSlideButtonDisabledProp('next', true);
  } else {
    setSlideButtonDisabledProp('next', false);
  }

  if (index === 0) {
    setSlideButtonDisabledProp('previous', true);
  } else {
    setSlideButtonDisabledProp('previous', false);
  }
};

const logProgress = (index) => {
  if (index > 0 && !store.logging.hasLoggedSlideShowStarted) {
    store.logging.hasLoggedSlideShowStarted = true;
    const slideshow = store.slideshow;
    const eventAttributes = {
      slideshow_id: slideshow.id,
      slideshow_created_at: slideshow.slideshow_created_at,
      slideshow_category: slideshare_object.gam_cat_name,
      slideshow_language: slideshow.slideshow_language,
      slideshow_format: slideshow.type,
      slideshow_started_definition: 'viewed_any_page_other_than_init',
    };

    logEvent('slideshow_started', eventAttributes);
  }

  if (hasFinishedMajorityOfSlideshow(index, store.totalSlides)) {
    const slideshow = store.slideshow;
    const eventAttributes = {
      slideshow_id: slideshow.id,
      slideshow_created_at: slideshow.slideshow_created_at,
      slideshow_category: slideshare_object.gam_cat_name,
      slideshow_language: slideshow.slideshow_language,
      slideshow_format: slideshow.type,
      slideshow_finished_definition:
        'viewed_' + FINISHED_MAJORITY_DEFINITION * 100 + '%',
    };

    logEvent('slideshow_finished', eventAttributes);
  }
};

const preventTrackerUpdate = () => {
  store.navigation.preventTrackerUpdate = true;
  setTimeout(() => {
    store.navigation.preventTrackerUpdate = false;
  }, 500);
}

const preventShowEndDisplay = () => {
  store.navigation.preventShowEndDisplay = true;
  setTimeout(() => {
    store.navigation.preventShowEndDisplay = false;
  }, 1000);
}

export const goToSlide = (newIndex, immediately = false) => {
  preventShowEndDisplay();
  if (immediately) {
    preventTrackerUpdate();
  }
  hideSlide(store.index);
  scrollToSlide(newIndex);
  showSlide(newIndex);
  handleSlideChange(newIndex);
};

const nextSlide = () => {
  const newIndex = store.index + 1;
  if (store.index < store.totalSlides - 1) {
    goToSlide(newIndex, true);
  } else {
    showEndDisplay();
  }
};

const previousSlide = () => {
  const newIndex = store.index - 1;
  if (store.index > 0) {
    goToSlide(newIndex, true);
  }

  hideEndDisplay();
};

export const hideMobileNavigation = () => {
  toolbar.removeClass('active');
};

export const showMobileNavigation = () => {
  toolbar.addClass('active');
};

const initializeResponsiveToolbar = () => {
  // For desktop, use mouseup/mousedown
  if (!isInfographic()) {
    let startX = 0
    stage.on("mousedown touchstart", (event) => {
      startX = event.pageX;
    });

    stage.on('mouseup touchend', (event) => {
      const xDistance = startX - event.pageX;
      if (Math.abs(xDistance) < 10) {
        // button response here, only if user is not swiping
        toggleInformationOverlay();
      }
    });
  }
}

const toggleInformationOverlay = () => {
  if (store.navigation.toolbarTimer) {
    clearTimeout(store.navigation.toolbarTimer);
  }
  if (toolbar.hasClass('active')) {
    hideMobileNavigation();
    store.navigation.toolbarTimer = null;
  } else {
    showMobileNavigation();
    store.navigation.toolbarTimer = setTimeout(() => {
      hideMobileNavigation();
    }, 5000);
  }
};

const intersectionObserverCallbackHandler = (cbExpectedCount, handleIntersectionSetup) => {
  return (entries) => {
    entries.forEach((entry, index) => {
      const {
        target,
        isIntersecting,
      } = entry;

      if (target && "parentNode" in target) {
        const canSetState = cbExpectedCount === 0;
        if (canSetState) {
          handleIntersectionSetup(target, isIntersecting)
        }
      }

      // Initially the cb is called once, we can start setting state 
      // only after all slides has been called at least once.
      if (cbExpectedCount > 0) {
        cbExpectedCount--;
      }
    });
  }
}


// For responsive player
const trackCurrentSlideResponsive = () => {
  if (!('IntersectionObserver' in window)) {
    console.warn("This browser doesn't support IntersectionObserver");
    return;
  }

  const options = {
    root: slideContainer[0],
    rootMargin: "0%",
    threshold: [0.99]
  };

  const cbExpectedCount = slides.length;

  const handleIntersectionSetup = (target, isIntersecting) => {
    if (isIntersecting) {
      const slideIndex = $(target).data('index');

      if ($(target).hasClass('slide-end-trap')) {
        handleEndReach(true)
      }else if (!store.navigation.preventTrackerUpdate) {
        handleSlideChangeDebouncedLeading(slideIndex);
      }
    } else {
      handleDraggingChange(true);
    }
  }

  const callbackFunction = intersectionObserverCallbackHandler(cbExpectedCount, handleIntersectionSetup);

  const observer = new window.IntersectionObserver(callbackFunction, options);
  for (const slideEl of slides) {
    observer.observe(slideEl);
  }
  observer.observe($('.slide-end-trap')[0]);
  return observer;
};

// For documents in desktop, vertical scroll track only
const trackCurrentSlideDesktop = () => {
  if (!('IntersectionObserver' in window)) {
    console.warn("This browser doesn't support IntersectionObserver");
    return;
  }

  const options = {
    root: stage[0],
    // Notes on rootMargin: We need to have like a thin line across 2/3rds of the player's height
    // that detects which is the current slide. It works like a scanner which detects the
    // current slide at 66% of player's height. This archives simplicty, good UX and
    // requires only single threshold (good performance, only one callback call).
    rootMargin: "-74% 0% -24% 0%",
    threshold: [0]
  };

  const cbExpectedCount = slides.length;

  const handleIntersectionSetup = (target, isIntersecting) => {
    if (isIntersecting) {
      const slideIndex = $(target).data('index');
      handleSlideChange(slideIndex);
    }
  }

  const callbackFunction = intersectionObserverCallbackHandler(cbExpectedCount, handleIntersectionSetup);

  const observer = new window.IntersectionObserver(callbackFunction, options);

  for (const slideEl of slides) {
    observer.observe(slideEl);
  }
  return observer;
};

// For documents and desktop only
const trackEndReachDesktop = () => {
  if (!('IntersectionObserver' in window)) {
    console.warn("This browser doesn't support IntersectionObserver");
    return;
  }

  const options = {
    root: stage[0],
    rootMargin: "0%",
    threshold: [0.9]
  };

  const cbExpectedCount = 1;

  const handleIntersectionSetup = (target, isIntersecting) => {
    if (isIntersecting) {
      handleEndReach(true);
    } else {
      handleEndReach(false);
    }
  }

  const callbackFunction = intersectionObserverCallbackHandler(cbExpectedCount, handleIntersectionSetup);

  const observer = new window.IntersectionObserver(callbackFunction, options);
  observer.observe($('.slide-end-trap')[0]);
  return observer;
};

const handleDraggingChange = (isDragging) => {
  if (isDragging) {
    hideMobileNavigation();
  }
}

const handleSlideChange = (newIndex) => {
  if (store.index !== newIndex) {
    logProgress(newIndex);
    updateIndex(newIndex);
    showSlide(newIndex);
    updateNavigation();
    updateProgressBar();
    updateClippingUI();
    handlePlayerBlur(newIndex);
  }
};

const handleSlideChangeDebouncedLeading = debounce(handleSlideChange, 300, { leading: false, trailing: true });

const handleEndReach = (isAtEndOfDocument) => {
  if (store.navigation.preventShowEndDisplay) {
    return;
  }

  if (isAtEndOfDocument && !store.endDisplay.isActive) {
    showEndDisplay();
  } else if (!isAtEndOfDocument && store.endDisplay.isActive) {
    hideEndDisplay();
  }
};

export const clearTrackers = () => {
  if (store.navigation.currentSlideDesktopTracker) {
    store.navigation.currentSlideDesktopTracker.disconnect();
    store.navigation.currentSlideDesktopTracker = null;
  }

  if (store.navigation.endReachDesktopTracker) {
    store.navigation.endReachDesktopTracker.disconnect();
    store.navigation.endReachDesktopTracker = null;
  }

  if (store.navigation.currentSlideMobileTracker) {
    store.navigation.currentSlideMobileTracker.disconnect();
    store.navigation.currentSlideMobileTracker = null;
  }
}

export const initializeTrackers = () => {
  clearTrackers();

  if ((isDesktop() || isEmbed()) && isDocument()) {
    store.navigation.currentSlideDesktopTracker = trackCurrentSlideDesktop();
    store.navigation.endReachDesktopTracker = trackEndReachDesktop();
  } else if (isMobile() && (isPresentation() || isDocument())) { // Reponsive non-document
    store.navigation.currentSlideMobileTracker = trackCurrentSlideResponsive();
  }
};

const initializeDesktopNavigation = () => {
  $('#new-player .leftpoint, #previous-slide').on('click', function () {
    previousSlide();
  });

  $('#new-player .rightpoint, #next-slide').on('click', function () {
    if (store.share.isOpen) {
      shareModalContainer.hide();
      store.share.isOpen = false;
    }
    nextSlide();
  });
};

$(document).on('keydown', function (e) {
  if (e.which === 37) {
    e.preventDefault();
    previousSlide();
  } else if (e.which === 39) {
    e.preventDefault();
    nextSlide();
  }
});

const configureStore = () => {
  store.navigation = {
    toolbarTimer: null,
    preventShowEndDisplay: null,
    currentSlideDesktopTracker: null,
    currentSlideMobileTracker: null,
    endReachDesktopTracker: null,
  };
};

const initializePlayerLoad = () => {
  // script is defered and may be already cached and load instantly
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeTrackers();
    });
  } else {
    // wait a tick to initialize trackers
    const asyncFunc = 'requestIdleCallback' in window ? window.requestIdleCallback : window.requestAnimationFrame
    asyncFunc(() => {
      initializeTrackers();
    })
  }

  // prevent resize to triger listeners callbacks before page fully load
  let resizeReady = false;
  window.addEventListener('load', () => {
    resizeReady = true;
  });

  window.addEventListener('resize', debounce(clearTrackers, 500, { leading: true, trailing: false }));
  window.addEventListener('resize', debounce(() => {
    if (!resizeReady) return;
    initializeTrackers();
  }, 500, { leading: false, trailing: true }));
}

export const initializeNavigation = () => {
  configureStore();
  initializeResponsiveToolbar();
  initializeDesktopNavigation();
  initializePlayerLoad();
};
